import _isEmpty from 'lodash/isEmpty';
import _debounce from 'lodash/debounce';
import _uniqBy from 'lodash/uniqBy';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import classnames from 'classnames';
import Select from 'components/v2/select/select';
import moment from 'moment';
import { format as dateFnsFormat } from 'date-fns';
import { Button } from 'grab-ui';
import DateRangePicker from 'components/v2/date-range-picker/date-range-picker';
import { getCompanyID, getCompanyName, getUserName } from 'helper/user';
import generateExcel from 'common/utils/generate-report-history-excel';
import { cat, custom, event, trackClick } from 'common/utils/instrumentation';
import {
  PAX_ORDER_HISTORY_FETCH_DAYS,
  PAX_HISTORY_FETCH_LIMIT,
  GFB_ORDER_HISTORY_FETCH_DAYS,
} from 'constants/history-constants.js';
import { FILTER_FIELD } from 'constants/booking-constants';
import {
  COMPLETED_ORDER_STATES,
  GE_WEB_MAP_GE_ORDER_STATE,
  GE_WEB_ORDER_STATE,
  ORDER_TYPES,
  ORDER_TYPES_SOURCE_ENUMS,
} from 'constants/order-constants';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_BOOKING_EVENTS,
  SCRIBE_SHOPIFY_EVENTS,
} from 'applications/scribe/instrumentations/track';
import ShopifyTrackEventContext from 'views/v2/shopify-home/track-event';
import shopifyLogo from 'public/images/shopify-logo.png';
import iconLoading from 'public/images/icon-loading.svg';

import OrderTableComplex from './order-table-complex/order-table-complex';
import css from './previous-bookings-section.less';

const readableDateFormat = 'D MMM YY'; // e.g. 1 Jan 2020

class PreviousBookingsSection extends Component {
  static contextType = ShopifyTrackEventContext;
  static getDerivedStateFromProps(props, state) {
    const {
      fetchedOrders,
      fetchedSourceOptions,
      fetchMoreLimit,
      resetFetchMore,
    } = state;
    const {
      isPaxUser,
      completedOrders,
      isPreviousBookingsLoading,
      completedOrdersSourceOptions,
      form: { getFieldValue },
    } = props;

    if (!isPaxUser || isPreviousBookingsLoading) return {};
    else if (resetFetchMore) {
      return {
        fetchedOrders: [],
        fetchedSourceOptions: [],
        fetchMoreLimit: PAX_HISTORY_FETCH_LIMIT,
        disableFetchMore: false,
        resetFetchMore: false,
      };
    }

    const newOrders = _uniqBy(
      [...fetchedOrders, ...completedOrders],
      'bookingCode',
    );
    const newSourceOptions = _uniqBy([
      ...fetchedSourceOptions,
      ...completedOrdersSourceOptions,
    ]);

    const dateRange = getFieldValue(FILTER_FIELD.DATE_RANGE);
    const isToday =
      !dateRange ||
      (dateRange && dateRange[1].isSameOrAfter(moment().startOf('day')));

    return {
      fetchedOrders: newOrders,
      fetchedSourceOptions: newSourceOptions,
      // since backend doesn't provide offset, each fetch more will increase limit
      // unless end date is today, disable fetch more button if it didn't pass the newest limit
      disableFetchMore: !isToday && newOrders.length < fetchMoreLimit,
    };
  }

  constructor(props) {
    super(props);
    const { isPaxUser, completedOrders, completedOrdersSourceOptions } = props;

    this.dayRange = isPaxUser
      ? PAX_ORDER_HISTORY_FETCH_DAYS
      : GFB_ORDER_HISTORY_FETCH_DAYS;
    this.initialFromDate = moment().subtract(this.dayRange, 'days');
    this.initialToDate = moment();

    this.state = {
      filterOrderType: ORDER_TYPES.ALL,
      filterOrderTypeEnum: -1, // ORDER_TYPES.ALL,
      filterOrderState: GE_WEB_ORDER_STATE.AllStatus,
      // states below used on pax users (with load more)
      fetchedOrders: [...completedOrders],
      fetchedSourceOptions: [...completedOrdersSourceOptions],
      fetchMoreLimit: PAX_HISTORY_FETCH_LIMIT,
      disableFetchMore: false,
    };
  }

  componentDidMount() {
    const { isPaxUser } = this.props;

    if (isPaxUser) {
      this.historyTableBodyRef = document.querySelector(
        '.ant-table-scroll .ant-table-body',
      );
      this.historyTableBodyRef.addEventListener('scroll', this.onScroll);
    }
  }

  componentWillUnmount() {
    const { isPaxUser } = this.props;
    if (isPaxUser) {
      window.scrollTo(0, 0);
      this.historyTableBodyRef.removeEventListener(
        'scroll',
        this.onScroll,
        false,
      );
    }
  }

  getOrderTypes = sources =>
    Object.keys(ORDER_TYPES_SOURCE_ENUMS).filter(
      key =>
        sources.find(source => source === ORDER_TYPES_SOURCE_ENUMS[key]) >= 0,
    );

  filtering = () => {
    const {
      isPaxUser,
      completedOrders,
      completedOrdersSourceOptions,
    } = this.props;
    const {
      filterOrderState,
      filterOrderTypeEnum,
      fetchedOrders,
      fetchedSourceOptions,
    } = this.state;

    const orders = isPaxUser ? fetchedOrders : completedOrders;
    const sources = isPaxUser
      ? fetchedSourceOptions
      : completedOrdersSourceOptions;
    const selectedOrderType = filterOrderTypeEnum >= 0;

    if (orders.length === 0) {
      return { orders: [], orderTypes: [] };
    } else if (
      filterOrderState === GE_WEB_ORDER_STATE.AllStatus &&
      !selectedOrderType
    ) {
      return { orders, orderTypes: this.getOrderTypes(sources) };
    }

    const data = [];
    const filterSources = selectedOrderType ? sources : []; // if order type is selected, disable adaptive (always show) show order type dropdown
    orders.forEach(item => {
      let stateFilter = true;
      let typeFilter = true;

      if (filterOrderState !== GE_WEB_ORDER_STATE.AllStatus) {
        stateFilter =
          item.status === filterOrderState ||
          (GE_WEB_MAP_GE_ORDER_STATE[filterOrderState] &&
            GE_WEB_MAP_GE_ORDER_STATE[filterOrderState].includes(item.status));
        // check GE_WEB_MAP_GE_ORDER_STATE mostly useful for pax orders
      }

      if (selectedOrderType) {
        typeFilter = item.source === filterOrderTypeEnum;
      }
      if (stateFilter && typeFilter) {
        data.push(item);
        if (!selectedOrderType && filterSources.indexOf(item.source) === -1) {
          filterSources.push(item.source);
        }
      }
    });

    return { orders: data, orderTypes: this.getOrderTypes(filterSources) };
  };

  onScroll = _debounce(
    () => {
      const { isPreviousBookingsLoading } = this.props;
      const clientHeight = this.historyTableBodyRef.clientHeight;
      const scrollTop = this.historyTableBodyRef.scrollTop;
      const scrollHeight = this.historyTableBodyRef.scrollHeight;
      if (
        !isPreviousBookingsLoading &&
        clientHeight + scrollTop >= scrollHeight - 60
      ) {
        this.fetchMoreCompletedOrders();
      }
    },
    150,
    false,
  );

  fetchMoreCompletedOrders = () => {
    const { fetchAllPreviousBookings, form: { getFieldValue } } = this.props;
    const { fetchMoreLimit } = this.state;

    const [fromDate, toDate] = getFieldValue(FILTER_FIELD.DATE_RANGE);
    const newFetchLimit = fetchMoreLimit + 20;
    fetchAllPreviousBookings({
      startTimestamp: fromDate.startOf('day').valueOf(),
      endTimestamp: toDate.endOf('day').valueOf(),
      limit: newFetchLimit,
    });

    this.setState({ fetchMoreLimit: newFetchLimit });
  };

  onClickExport = orders => () => {
    const {
      userInfo,
      ongoingOrdersLastUpdate,
      form: { getFieldValue },
    } = this.props;
    const { filterOrderState } = this.state;
    const companyID = getCompanyID(userInfo);
    trackClick(event.BOOKINGS_CLICKS, cat.GE_BOOKINGS, 'PAST_BOOKINGS_CSV', {
      [custom.COMPANY_ID]: companyID,
    });
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
      eventName: SCRIBE_BOOKING_EVENTS.PAST_BOOKINGS_CSV,
      facets: getCommonFacet(userInfo),
    });

    const [fromDate, toDate] = getFieldValue(FILTER_FIELD.DATE_RANGE);
    const currentDate = dateFnsFormat(new Date(), 'dd LLL yyyy');

    generateExcel(
      getUserName(userInfo),
      getCompanyName(userInfo),
      fromDate.format(readableDateFormat),
      toDate.format(readableDateFormat),
      filterOrderState,
      `${ongoingOrdersLastUpdate} ${currentDate}`,
      orders,
      userInfo.selectedCity,
    );
  };

  getPreviousOrderStateToRender = () =>
    COMPLETED_ORDER_STATES.map(state => ({
      value: state.state,
      title: I18n.t(state.text),
      child: I18n.t(state.text),
    }));

  disabledDate = dateToBeTested => {
    const afterToday = dateToBeTested > moment().endOf('day');

    if (this.newStartDate) {
      const longerThanDayRange = dateToBeTested.isAfter(
        moment(this.newStartDate).add(this.dayRange, 'days'),
      );
      const beforeStartDate = dateToBeTested.isBefore(
        moment(this.newStartDate),
      );
      return afterToday || longerThanDayRange || beforeStartDate;
    }

    return afterToday;
  };

  onCalendarChange = dates => {
    this.newStartDate = dates[0];
  };

  newDateSelectionComplete = () => {
    const { userInfo, isPaxUser } = this.props;
    const companyID = getCompanyID(userInfo);

    if (!isPaxUser) {
      trackClick(event.BOOKINGS_CLICKS, cat.GE_BOOKINGS, 'DATE_RANGE', {
        [custom.COMPANY_ID]: companyID,
      });
    }

    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
      eventName: SCRIBE_BOOKING_EVENTS.DATE_RANGE,
      facets: getCommonFacet(userInfo),
    });

    this.newStartDate = null;
  };

  getPreviousOrderTypesToRender = orderTypes => {
    let options = [
      {
        value: ORDER_TYPES.ALL,
        title: I18n.t('history.orderTypes.all'),
      },
    ];
    options = options.concat(
      Object.keys(ORDER_TYPES)
        .filter(ot => orderTypes.find(o => o === ot))
        .map(ot => ({
          value: ot,
          title: I18n.t(`history.orderTypes.${ot.toLowerCase()}`),
        })),
    );
    return options.map(opt => ({
      value: opt.value,
      title: opt.title,
      child: opt.title,
      icon: opt.value === ORDER_TYPES.SHOPIFY ? shopifyLogo : undefined,
    }));
  };

  onChangeOrderType = value => {
    const { isPaxUser } = this.props;

    if (!isPaxUser && value === ORDER_TYPES.SHOPIFY) {
      this.context.fireShopifyTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
        eventName: SCRIBE_SHOPIFY_EVENTS.FILTER_SHOPIFY_ORDERS,
      });
    }

    const sourceEnum = ORDER_TYPES_SOURCE_ENUMS[value];
    this.setState({
      filterOrderType: value,
      filterOrderTypeEnum: sourceEnum >= 0 ? sourceEnum : -1,
    });
  };

  onChangeOrderState = value => {
    const { userInfo, isPaxUser } = this.props;
    const companyID = getCompanyID(userInfo);

    if (!isPaxUser) {
      trackClick(
        event.BOOKINGS_CLICKS,
        cat.GE_BOOKINGS,
        'PAST_BOOKING_STATUS',
        {
          [custom.COMPANY_ID]: companyID,
        },
      );
    }

    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
      eventName: SCRIBE_BOOKING_EVENTS.PAST_BOOKINGS_STATUS,
      facets: getCommonFacet(userInfo),
    });

    this.setState({ filterOrderState: value });
  };

  getTableProps = () => {
    const { isPaxUser, ongoingOrders, ongoingOrdersLastUpdate } = this.props;

    if (isPaxUser) {
      return {
        pagination: false,
        scroll: { y: 400 },
        className: classnames(css.completedOrderTable, css.noMarginBottom),
      };
    }
    return {
      className: css.completedOrderTable,
      pagination: { pageSize: 10 },
      ongoingOrders,
      ongoingOrdersLastUpdate,
      onClickExport: this.onClickExport,
    };
  };

  render() {
    const {
      userInfo,
      updateOrderShallow,
      bookAgain,
      shopifyBookAgain,
      form,
      isPreviousBookingsLoading,
      isPaxUser,
    } = this.props;
    const { filterOrderType, filterOrderState, disableFetchMore } = this.state;
    const { orders, orderTypes } = this.filtering();

    const previousOrderStates = this.getPreviousOrderStateToRender();
    const previousOrderTypes = this.getPreviousOrderTypesToRender(orderTypes);
    const tableProps = this.getTableProps();

    return (
      <Fragment>
        <div className={css.sectionHeader}>
          <div>
            <h5>{I18n.t('v2.bookingsPage.pastBookings')}</h5>
          </div>
          <div className={css.previousOrderFilterSection}>
            {previousOrderTypes.length > 2 && (
              <Select
                placeholder=""
                options={previousOrderTypes}
                className={css.orderTypeSelect}
                onChange={this.onChangeOrderType}
                value={filterOrderType}
                disabled={isPreviousBookingsLoading}
              />
            )}
            <Select
              placeholder=""
              options={previousOrderStates}
              className={css.orderStatusSelect}
              onChange={this.onChangeOrderState}
              value={filterOrderState}
              disabled={isPreviousBookingsLoading}
            />
            <DateRangePicker
              id={FILTER_FIELD.DATE_RANGE}
              form={form}
              fieldOptions={{
                initialValue: [this.initialFromDate, this.initialToDate],
              }}
              disabledDate={this.disabledDate}
              format={readableDateFormat}
              fieldClassName={classnames(
                css.dateRangePicker,
                isPaxUser ? css.noMarginBottom : null,
              )}
              allowClear={false}
              disabled={isPreviousBookingsLoading}
              onCalendarChange={this.onCalendarChange}
              onChange={this.newDateSelectionComplete}
            />
            {!isPaxUser && (
              <Button
                onClick={this.onClickExport(orders)}
                disabled={isPreviousBookingsLoading || _isEmpty(orders)}
              >
                {I18n.t('v2.bookingsPage.exportExcel')}
              </Button>
            )}
          </div>
        </div>
        <OrderTableComplex
          userInfo={userInfo}
          updateOrderShallow={updateOrderShallow}
          bookAgain={bookAgain}
          shopifyBookAgain={shopifyBookAgain}
          loading={isPreviousBookingsLoading}
          orders={orders}
          {...tableProps}
        />
        {isPaxUser && (
          <div className={css.sectionFooter}>
            <img
              className={
                isPreviousBookingsLoading
                  ? css.loadingIcon
                  : css.loadingIconHidden
              }
              src={iconLoading}
              alt="loading"
            />
            {!isPreviousBookingsLoading && (
              <span
                onClick={this.fetchMoreCompletedOrders}
                className={
                  disableFetchMore ? css.fetchMoreBtnDisabled : css.fetchMoreBtn
                }
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                role="button"
                tabIndex="0"
                onKeyDown={null}
              >
                {I18n.t('booking.loadMore')}
              </span>
            )}
          </div>
        )}
      </Fragment>
    );
  }
}

PreviousBookingsSection.propTypes = {
  userInfo: PropTypes.shape({}).isRequired,
  ongoingOrders: PropTypes.arrayOf(PropTypes.shape({})),
  ongoingOrdersLastUpdate: PropTypes.string,
  completedOrders: PropTypes.arrayOf(PropTypes.shape({})),
  completedOrdersSourceOptions: PropTypes.arrayOf(PropTypes.number),
  updateOrderShallow: PropTypes.func.isRequired,
  bookAgain: PropTypes.func.isRequired,
  shopifyBookAgain: PropTypes.func.isRequired,
  form: PropTypes.shape({}).isRequired,
  isPreviousBookingsLoading: PropTypes.bool.isRequired,
  fetchAllPreviousBookings: PropTypes.func,
  isPaxUser: PropTypes.bool, // will use load more instead of pagination
};

PreviousBookingsSection.defaultProps = {
  ongoingOrders: [],
  ongoingOrdersLastUpdate: '',
  completedOrders: [],
  completedOrdersSourceOptions: [],
  fetchAllPreviousBookings: undefined,
  isPaxUser: false,
};

export default PreviousBookingsSection;
